import { useEffect, useState } from 'react';
import { getFileBlobUrl } from '@/api/query/file';
import { normalizeFileId } from '@/common/hooks/useImage';

const fileCache = new Map();
const pendingLoads = new Map();

const loadStoryFile = async (normalizedFileId, expectedMediaType = null) => {
	if (fileCache.has(normalizedFileId)) {
		return fileCache.get(normalizedFileId);
	}

	if (pendingLoads.has(normalizedFileId)) {
		return pendingLoads.get(normalizedFileId);
	}

	const request = getFileBlobUrl(normalizedFileId, expectedMediaType)
		.then((result) => {
			if (result?.url) {
				fileCache.set(normalizedFileId, result);
			}

			return result;
		})
		.catch(() => null)
		.finally(() => {
			pendingLoads.delete(normalizedFileId);
		});

	pendingLoads.set(normalizedFileId, request);

	return request;
};

const useStoryFileLoader = (fileId, expectedMediaType = null) => {
	const normalizedFileId = normalizeFileId(fileId);
	const cached = normalizedFileId ? fileCache.get(normalizedFileId) : null;
	const [fileSrc, setFileSrc] = useState(cached?.url ?? null);
	const [contentType, setContentType] = useState(cached?.contentType ?? null);
	const [isLoading, setIsLoading] = useState(
		Boolean(normalizedFileId && !cached),
	);

	useEffect(() => {
		if (!normalizedFileId) {
			setFileSrc(null);
			setContentType(null);
			setIsLoading(false);
			return;
		}

		const cachedFile = fileCache.get(normalizedFileId);

		if (cachedFile) {
			setFileSrc(cachedFile.url);
			setContentType(cachedFile.contentType);
			setIsLoading(false);
			return;
		}

		let isActive = true;
		setIsLoading(true);
		setFileSrc(null);
		setContentType(null);

		loadStoryFile(normalizedFileId, expectedMediaType).then((result) => {
			if (!isActive) {
				return;
			}

			setFileSrc(result?.url ?? null);
			setContentType(result?.contentType ?? null);
			setIsLoading(false);
		});

		return () => {
			isActive = false;
		};
	}, [normalizedFileId, expectedMediaType]);

	return { fileSrc, contentType, isLoading };
};

export default useStoryFileLoader;
